import { Injectable } from '@angular/core';
import { SubHeaderService } from '../../store/subheader/subheader.service';
import { CommissionService } from '../../store/commissions/commission/commission.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SearchService } from '../../store/search/search.service';
import { map, switchMap, take, takeWhile } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, Observable, of } from 'rxjs';
import { filterTrue } from '../../pipe/rxjs/operators';
import { Filter } from '../../store/search/types';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class RowSelectionService {
  constructor(
    private readonly subHeaderService: SubHeaderService,
    private readonly commissionService: CommissionService,
    private readonly searchService: SearchService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {
    this.subHeaderService
      .getTriggerAction()
      .pipe(untilDestroyed(this))
      .subscribe((x) => {
        if (x == 'createSalesPersonCommissionAccountMoves')
          this.createSalesPersonCommissionAccountMoves();
        if (x == 'createPaymentSalesPersonCommission')
          this.createPaymentSalesPersonCommission();

        if (x == 'deletePaymentSalesPersonCommission')
          this.deletePaymentSalesPersonCommission();

        if (x == 'deletePaymentSalesPersonCommissionPayment')
          this.deletePaymentSalesPersonCommissionPayment();

        this.subHeaderService.setTriggerAction('');
      });
  }

  private createSalesPersonCommissionAccountMoves() {
    this.searchService
      .getSelectedRows()
      .pipe(take(1))
      .subscribe((selectedRows) => {
        if (selectedRows.data.length == 0) return;

        const result$ = this.commissionService.add.call({
          commissionPreviewModels: selectedRows.data,
          isTotalRowCountSelected: selectedRows.isTotalRowCountSelected,
        });

        result$
          .pipe(
            takeWhile((x) => !x, true),
            filter((x) => !!x),
          )
          .subscribe((result) => {
            this.router.navigate([
              '/Finance/SalesPersonCommissions/AccountMoves/List',
            ]);
            this.commissionService.add.reset();
          });
      });
  }

  private createPaymentSalesPersonCommission() {
    this.getAllSelectedRowsWithFilters$().subscribe(
      ({ selectedRows, filters }) => {
        const result$ =
          this.commissionService.addSalesPersonCommissionPayment.call({
            salesPersonCommissions: selectedRows.data,
            filters: filters,
            isTotalCountSelected: selectedRows.isTotalRowCountSelected,
          });

        result$
          .pipe(
            takeWhile((x) => x != false && x != true, true),
            filter((x) => x == true || x == false),
          )
          .subscribe((result) => {
            this.commissionService.addSalesPersonCommissionPayment.reset();
          });
      },
    );
  }

  private deletePaymentSalesPersonCommission() {
    this.getAllSelectedRowsWithFilters$().subscribe(
      ({ selectedRows, filters }) => {
        const result$ =
          this.commissionService.deleteSalesPersonCommissions.call({
            salesPersonCommissions: selectedRows.data,
            filters: filters,
            isTotalCountSelected: selectedRows.isTotalRowCountSelected,
          });

        result$
          .pipe(
            takeWhile((x) => x != false && x != true, true),
            filter((x) => x == true || x == false),
          )
          .subscribe((result) => {
            this.commissionService.deleteSalesPersonCommissions.reset();
          });
      },
    );
  }

  private deletePaymentSalesPersonCommissionPayment() {
    this.searchService
      .getSelectedRows()
      .pipe(take(1))
      .subscribe((selectedRows) => {
        if (selectedRows.data.length == 0) return;

        const result$ =
          this.commissionService.deleteSalesPersonCommissionPayment.call(
            selectedRows.data.map((x) => x.id),
          );

        result$
          .pipe(
            takeWhile((x) => x != false && x != true, true),
            filter((x) => x == true || x == false),
          )
          .subscribe((result) => {
            this.commissionService.deleteSalesPersonCommissionPayment.reset();
          });
      });
  }

  private getAllSelectedRowsWithFilters$(): Observable<{
    selectedRows: any; // type returned by searchService.getSelectedRows()
    filters: Filter[];
  }> {
    return this.searchService.getSelectedRows().pipe(
      take(1),
      filter((rows) => rows.data.length > 0 || rows.isTotalRowCountSelected),
      switchMap((rows) =>
        (rows.isTotalRowCountSelected
          ? this.searchService.getActiveFilters().pipe(take(1))
          : of<Filter[]>([])
        ).pipe(map((filters) => ({ selectedRows: rows, filters }))),
      ),
    );
  }
}
